/* eslint-disable import/no-internal-modules */
import { Col, Row } from 'antd';
import QueueAnim from 'rc-queue-anim';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ISingleValue as SingleValue } from 'components/atoms/select';
import { useHubspotExternalResourceFields } from 'queries/hubspot-integration';
import { sortByText } from 'utils/sort-by-text';
import {
  HubspotFieldMappingLocalData,
  HubspotResourceMappingLocalData,
  PartialHubspotFieldMappingLocalData,
} from '../../types';
import { FieldMapping } from '../field-mapping';
import { EmptyState } from './empty-state';
import { Header } from './header';
import { Loader } from './loader';

const Container = styled.div`
  margin-bottom: var(--layout-03);
`;

type FieldsObjectProps = {
  integrationId: number;
  fieldObject: HubspotResourceMappingLocalData;
  onObjectRemove: (resourceId: number) => void;
  onMappingAdd: (resourceId: number, externalField: SingleValue) => void;
  onMappingRemove: (
    resourceId: number,
    fieldMapping: HubspotFieldMappingLocalData,
  ) => void;
  onMappingChange: (
    resourceId: number,
    mappingId: string,
    payload: PartialHubspotFieldMappingLocalData,
  ) => void;
};

export const FieldsObject = ({
  integrationId,
  fieldObject,
  onObjectRemove,
  onMappingAdd,
  onMappingRemove,
  onMappingChange,
}: FieldsObjectProps): JSX.Element => {
  const { data: externalFields = [], status: externalFieldsStatus } =
    useHubspotExternalResourceFields(
      integrationId,
      fieldObject.externalResourceType,
    );

  const selectedExternalFieldsById = useMemo(
    () =>
      (fieldObject.fieldMappings ?? [])
        .map((fieldMapping) => fieldMapping.hubspotField?.value)
        .filter(Boolean),
    [fieldObject],
  );

  const externalFieldAsOptions = useMemo(
    () =>
      (externalFields ?? [])
        .filter((field) =>
          field?.allowMultipleMapping
            ? field.allowMultipleMapping
            : !selectedExternalFieldsById.includes(field.integrationFieldId),
        )
        .map((field) => ({
          value: field.integrationFieldId,
          label: field.fieldName,
          extra: field.editableByFieldMapping,
          parent: field.parentFieldName ?? fieldObject.externalResourceType,
        }))
        .sort((a, b) => sortByText(a.label, b.label)),
    [externalFields, selectedExternalFieldsById, fieldObject],
  );

  const onExternalFieldSelect = (selected: SingleValue) => {
    if (selected) {
      onMappingAdd(fieldObject.resourceMappingConfigId, {
        label:
          selected.parent === fieldObject.externalResourceType
            ? selected.label
            : `[${selected.parent}] ${selected.label}`,
        value: selected.value.toString(),
        extra: selected.extra,
      });
    }
  };

  return (
    <Container>
      <Header
        name={fieldObject.externalResourceType}
        onRemove={() => onObjectRemove(fieldObject.resourceMappingConfigId)}
      />
      <Row>
        <Col span={24}>
          {externalFieldsStatus === 'loading' ? <Loader /> : null}
          {externalFieldsStatus === 'success' &&
          fieldObject.fieldMappings?.length ? (
            <QueueAnim component="div" type={['right', 'left']} leaveReverse>
              {fieldObject.fieldMappings.map((fieldMapping) =>
                fieldMapping && fieldMapping.hubspotField ? (
                  <FieldMapping
                    integrationId={integrationId}
                    externalFields={externalFieldAsOptions}
                    fieldMap={fieldMapping}
                    onRemove={() =>
                      onMappingRemove(
                        fieldObject.resourceMappingConfigId,
                        fieldMapping,
                      )
                    }
                    hubspotFieldValue={fieldMapping.hubspotField.value.toString()}
                    onChange={onMappingChange}
                    key={fieldMapping.fieldMappingConfigId ?? fieldMapping.id}
                    fieldObject={fieldObject}
                  />
                ) : null,
              )}
            </QueueAnim>
          ) : null}
        </Col>
      </Row>

      <EmptyState
        fields={externalFieldAsOptions}
        onChange={onExternalFieldSelect}
        fieldObject={fieldObject}
      />
    </Container>
  );
};
